// apps/web/src/features/users/components/ChangePasswordSelfModal.tsx
// Spec §14.5. RHF + local FormSchema that extends ChangePasswordInputSchema
// with a confirmNewPassword field validated client-side only. On success,
// clears the auth store, surfaces a localized success toast, and navigates
// to /login. On 401 AUTH_INVALID_CREDENTIALS, errorMessage(err,
// 'change-password') localizes the current-password error.
//
// Issue #628 PR3 (now #680): close paths (Modal chrome X, Escape, backdrop)
// route through the EditorModal wrapper guard. SECURITY: the wrapper
// receives only a boolean `dirty` (derived locally from RHF
// `formState.isDirty` — empty strings are the baseline, so any keystroke
// marks dirty), a boolean `busy` from the mutation, and the close
// callback. Password field VALUES never cross the guard boundary.
// Successful change resets the form so the post-success `onClose()`
// (called implicitly via navigate) would not trip a stale-dirty warning.
import { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useChangePassword } from '@magimp/shared-hooks';
import { EditorModal, Button, FormRow, useEditorGuard } from '../../../ui/index.js';
import { PasswordField } from '../../auth/components/PasswordField.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { clearWithBroadcast } from '../../../api/authEvents.js';

// UI form. The shared ChangePasswordInputSchema is a ZodEffects-wrapped
// z.object that validates `currentPassword`, `newPassword`, and refines on
// "newPassword must differ from currentPassword". The form adds a client-
// only `confirmNewPassword` field. Re-authoring the constraints inline
// keeps the form a single ZodObject (so .refine() chains cleanly); the
// wire payload strips confirmNewPassword before calling mutate().
const PasswordInput = z.string().min(12).max(256);
const createFormSchema = (messages: {
  confirmRequired: string;
  newMustDiffer: string;
  mismatch: string;
}) =>
  z
    .object({
      currentPassword: PasswordInput,
      newPassword: PasswordInput,
      confirmNewPassword: z.string().min(1, messages.confirmRequired),
    })
    .refine((v) => v.newPassword !== v.currentPassword, {
      message: messages.newMustDiffer,
      path: ['newPassword'],
    })
    .refine((v) => v.confirmNewPassword === v.newPassword, {
      message: messages.mismatch,
      path: ['confirmNewPassword'],
    });
type FormShape = z.infer<ReturnType<typeof createFormSchema>>;

export interface ChangePasswordSelfModalProps {
  onClose: () => void;
}

export function ChangePasswordSelfModal({ onClose }: ChangePasswordSelfModalProps) {
  const { t } = useTranslation(['users', 'common', 'shell']);
  const schema = useMemo(
    () =>
      createFormSchema({
        confirmRequired: t('users:password.confirmRequired'),
        mismatch: t('users:password.mismatch'),
        newMustDiffer: t('users:password.newMustDiffer'),
      }),
    [t],
  );
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormShape>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  });
  const { mutate, isPending } = useChangePassword();

  return (
    <EditorModal
      open={true}
      title={t('users:modals.changePasswordTitle')}
      dirty={isDirty}
      busy={isPending}
      onConfirmClose={onClose}
      width={480}
    >
      <ChangePasswordForm
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        reset={reset}
        mutate={mutate}
        isPending={isPending}
      />
    </EditorModal>
  );
}

// WHY: form body is split out so `useEditorGuard().bypassNext` can be called
// from inside the EditorModal subtree on the `onSuccess` -> `navigate('/login')`
// path. RHF `reset()` clears `isDirty` for the NEXT render, but the synchronous
// `navigate(...)` below trips the route blocker before that render commits;
// `bypassNext()` is the documented one-shot escape for this pattern.
function ChangePasswordForm({
  control,
  errors,
  handleSubmit,
  reset,
  mutate,
  isPending,
}: {
  control: ReturnType<typeof useForm<FormShape>>['control'];
  errors: ReturnType<typeof useForm<FormShape>>['formState']['errors'];
  handleSubmit: ReturnType<typeof useForm<FormShape>>['handleSubmit'];
  reset: ReturnType<typeof useForm<FormShape>>['reset'];
  mutate: ReturnType<typeof useChangePassword>['mutate'];
  isPending: boolean;
}) {
  const { t } = useTranslation(['users', 'common', 'shell']);
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);
  const { bypassNext } = useEditorGuard();

  const onSubmit = (values: FormShape) => {
    const { confirmNewPassword: _omit, ...payload } = values;
    void _omit;
    mutate(payload, {
      onSuccess: () => {
        // Server-side: changing the password invalidates other sessions on
        // the next refresh. Broadcast PASSWORD_CHANGED so sister tabs
        // converge to logged-out without waiting for that round-trip.
        clearWithBroadcast('PASSWORD_CHANGED');
        // Re-baseline so any unmount/navigation does not see stale dirty.
        reset({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
        // RHF `reset()` clears formState.isDirty for the next render, but
        // the synchronous `navigate('/login')` below trips the blocker
        // before that render commits. `bypassNext()` is the documented
        // one-shot escape for the onSuccess-then-navigate pattern.
        bypassNext();
        showToast({ tone: 'success', text: t('users:toasts.changePasswordSuccess') });
        navigate('/login');
      },
      onError: (err) => {
        showToast({ tone: 'error', text: errorMessage(err, 'change-password') });
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormRow label={t('users:fields.currentPassword')} error={errors.currentPassword?.message}>
        <Controller
          name="currentPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              value={field.value}
              onChange={field.onChange}
              autoComplete="current-password"
            />
          )}
        />
      </FormRow>
      <FormRow label={t('users:fields.newPassword')} error={errors.newPassword?.message}>
        <Controller
          name="newPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              value={field.value}
              onChange={field.onChange}
              hint={t('users:password.requirementWithDifference')}
              showGenerate
              showCopy
              autoComplete="new-password"
            />
          )}
        />
      </FormRow>
      <FormRow label={t('users:password.confirmNew')} error={errors.confirmNewPassword?.message}>
        <Controller
          name="confirmNewPassword"
          control={control}
          render={({ field }) => (
            <PasswordField
              value={field.value}
              onChange={field.onChange}
              autoComplete="new-password"
            />
          )}
        />
      </FormRow>
      <Button type="submit" kind="primary" disabled={isPending}>
        {t('users:actions.changePassword')}
      </Button>
    </form>
  );
}
